import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {HorribleHeroesLayerConfig} from "../config/IHorribleHeroesLayerConfig";
import {HorribleHeroesGameConfig} from "../HorribleHeroesGameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {TimelineLite} from "gsap";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {HorribleHeroesParsedGameData} from "../server/data/HorribleHeroesParsedGameData";
import {HorribleHeroesParsedInitData} from "../server/data/HorribleHeroesParsedInitData";
import {SoundManager} from "../resource/sound/SoundManager";

export class ScatterCollectMeterView extends BaseView {
    protected static meterStates: { [stateName: string]: string } = {
        "state0": "deactive",
        "state1": "active1",
        "state2": "active2",
        "state3": "active3",
        "state4": "active4",
        "state5": "active5",
    };
    private readonly _layer: ScalableLayer;
    private readonly _layerPortrait: ScalableLayer;
    private _scatterCollecMeterDesktop: PIXI.spine.Spine;
    private _scatterCollecMeterMobile: PIXI.spine.Spine;
    private mayhemMeterDesktopBlast: PIXI.spine.Spine;
    private mayhemMeterMobileBlast: PIXI.spine.Spine;
    private _state: number = 0;
    private _scatterOnRestore: number = 0;
    private _isSuperFSMode: boolean = false;

    constructor() {
        super();
        this._layer = StageManager.getLayer(HorribleHeroesLayerConfig.SCATTER_METER.name);
        this._layerPortrait = StageManager.getLayer(HorribleHeroesLayerConfig.SCATTER_METER_PORTRAIT.name);
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onGameDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event: GameEvent) => this.onGameDataParsed(event.params[0]));
    }

    public playScatterCollectState(scatterCollected: number, progress?: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this._state === scatterCollected && scatterCollected > 0) {
            return tl;
        }
        if (scatterCollected === 0) {
            this.resetMeters();
        }
        let playState: string = ScatterCollectMeterView.meterStates["state" + scatterCollected];
        if (this._scatterCollecMeterMobile.visible) {
            this._state = scatterCollected;
            this.playSpineAnimation(this._scatterCollecMeterMobile, this._layerPortrait, playState, progress);
        } else if (this._scatterCollecMeterDesktop.visible) {
            this._state = scatterCollected;
            this.playSpineAnimation(this._scatterCollecMeterDesktop, this._layer, playState, progress);
        }
        const animDuration: number = this._scatterCollecMeterDesktop.spineData.findAnimation(playState).duration;
        if (scatterCollected === 5) {
            const duration: number = this.mayhemMeterDesktopBlast.spineData.findAnimation("blast").duration;
            tl.add(() => {
                this.playMayhemBlast(progress);

                // #454, #480 -> don't play this on restore
                if (this._scatterOnRestore === 0) {
                    SoundManager.playFSMeterExplode();
                }
            }, duration);
        } else {
            tl.add(() => {

            }, animDuration);
        }
        return tl;
    }

    protected initAnimations(): void {
        this.createGraphics();
    }

    /*reset default xways on win*/
    protected onResize(resizeData: IResizeData): void {
        if (resizeData.orientation === Orientation.PORTRAIT) {
            this._scatterCollecMeterMobile.visible = true;
            this._scatterCollecMeterDesktop.visible = false;
        } else {
            this._scatterCollecMeterDesktop.visible = true;
            this._scatterCollecMeterMobile.visible = false;
        }
        if (this.mayhemMeterMobileBlast.visible || this.mayhemMeterDesktopBlast.visible) {
            if (resizeData.orientation === Orientation.PORTRAIT) {
                this.mayhemMeterMobileBlast.visible = true;
                this.mayhemMeterDesktopBlast.visible = false;
            } else {
                this.mayhemMeterMobileBlast.visible = false;
                this.mayhemMeterDesktopBlast.visible = true;
            }
        }
        let state: number = this._state;
        this._scatterOnRestore > 0 ? state = this._scatterOnRestore : state = this._state;
        this._state = 0;
        if (this._isSuperFSMode) {
            this.playMayhemBlast(1);
        } else {
            this.playScatterCollectState(state, 1);
        }
    }

    private playMayhemBlast(progress?: number): void {
        this._state = 5;
        if (this._scatterCollecMeterMobile.visible) {
            this._scatterCollecMeterMobile.visible = false;
            this.playSpineAnimation(this.mayhemMeterMobileBlast, this._layerPortrait, "blast", progress);
        } else if (this._scatterCollecMeterDesktop.visible) {
            this._scatterCollecMeterDesktop.visible = false;
            this.playSpineAnimation(this.mayhemMeterDesktopBlast, this._layer, "blast", progress);
        }
    }

    //#445
    private playSpineAnimation(spineAnimation: PIXI.spine.Spine, Layer: ScalableLayer, animName: string, progress?: number): TimelineLite {
        Layer.addChild(spineAnimation);
        const tl: TimelineLite = new TimelineLite();
        spineAnimation.updateTransform();
        spineAnimation.visible = true;
        spineAnimation.state.setAnimation(0, animName, false);
        if (progress) {
            const trackEntry: PIXI.spine.core.TrackEntry = spineAnimation.state.getCurrent(0);
            //=> 0 is the track index at which animation was/is played
            trackEntry.trackTime = trackEntry.animation.duration;
        }
        return tl;
    }

    private resetMeters(): void {
        if (this.mayhemMeterMobileBlast.visible) {
            this.mayhemMeterMobileBlast.visible = false;
            this._scatterCollecMeterMobile.visible = true;
            this._layerPortrait.removeChild(this.mayhemMeterMobileBlast);
        } else {
            this.mayhemMeterDesktopBlast.visible = false;
            this._scatterCollecMeterDesktop.visible = true;
            this._layer.removeChild(this.mayhemMeterDesktopBlast);
        }
    }

    private onGameDataParsed(data: HorribleHeroesParsedGameData | HorribleHeroesParsedInitData): void {
        if (data instanceof HorribleHeroesParsedInitData) {
            EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
            this._scatterOnRestore = data.getScattersCollected;
        } else {
            this._scatterOnRestore = 0;
            this._isSuperFSMode = data.isSuperActivatedSpin;
        }
    }

    private createGraphics(): void {
        this._scatterCollecMeterDesktop = this.createSpine("mayhemMeterDesktop");
        this.mayhemMeterDesktopBlast = this.createSpine("mayhemMeterDesktopBlast");
        this._scatterCollecMeterMobile = this.createSpine("mayhemMeterMobile");
        this.mayhemMeterMobileBlast = this.createSpine("mayhemMeterMobileBlast");
        this._layer.addChild(this._scatterCollecMeterDesktop);
        this._layerPortrait.addChild(this._scatterCollecMeterMobile);
        this._scatterCollecMeterDesktop.position.set(HorribleHeroesGameConfig.instance.REEL_AREA_WIDTH + 53, HorribleHeroesGameConfig.instance.REEL_AREA_POS_Y + 205);
        this._scatterCollecMeterMobile.position.set(186, HorribleHeroesGameConfig.instance.REEL_AREA_POS_Y - 7);
        this.mayhemMeterMobileBlast.position = this._scatterCollecMeterMobile.position;
        this.mayhemMeterDesktopBlast.position = this._scatterCollecMeterDesktop.position;
        this.playScatterCollectState(this._scatterOnRestore, 1);
        this._scatterOnRestore === 5 ? this.playMayhemBlast(1) : "";
        /*to handle restore*/
    }

    private createSpine(spineAsst: string): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset(spineAsst));
        spine.name = spineAsst;
        spine.visible = false;
        return spine;
    }
}